import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import LastUpdatedBadge from '../../components/common/LastUpdatedBadge';
import TableOfContents from '../../components/common/TableOfContents';
import useSEO from '../../hooks/useSEO';

const LAST_UPDATED = 'March 12, 2026';

export default function PrivacyPolicyPage() {
  const contentRef = useRef(null);
  useSEO({
    title: 'Privacy Policy · U-DORM',
    description: 'How U-DORM collects, uses, stores, and protects your personal data. Updated March 2026.',
    image: '/logo.png',
  });
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F5DC] to-[#E8E4D9] dark:from-gray-900 dark:to-gray-800">
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#2F4F4F] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">U</span>
              </div>
              <span className="text-xl font-bold text-[#2F4F4F] dark:text-white">U-DORM</span>
            </Link>
            <div className="flex items-center gap-3">
              <LastUpdatedBadge date={LAST_UPDATED} />
              <Link to="/">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-8">
        <TableOfContents contentRef={contentRef} />
        <div ref={contentRef} className="flex-1 min-w-0 bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 md:p-12 prose prose-gray dark:prose-invert max-w-none text-justify hyphens-auto">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">Last updated: {LAST_UPDATED}</p>

          <p>MindfulUX ("Company," "we," "us," or "our") operates the U-DORM platform ("Platform"). This Privacy Policy describes how we collect, use, disclose, and protect your personal information when you use our Services. This policy applies to all Users worldwide.</p>

          <h2>1. Information We Collect</h2>
          <h3>1.1 Information You Provide</h3>
          <ul>
            <li><strong>Account Information:</strong> Name, email address, phone number, password, and role (Landlord, Tenant, Property Manager).</li>
            <li><strong>Profile Information:</strong> Date of birth, home address, occupation, student status, school name, student ID, emergency contact details, and profile photo.</li>
            <li><strong>Property Information:</strong> Property names, addresses, descriptions, unit details, and property images.</li>
            <li><strong>Lease Information:</strong> Lease terms, rental amounts, start and end dates, additional fees, and house rules.</li>
            <li><strong>Payment Information:</strong> Payment amounts, dates, methods (e.g., GCash, Maya, bank transfer, cash), payment proofs (uploaded images), and receipt data. <strong>We do not process or store credit card numbers or bank account details.</strong></li>
            <li><strong>Communications:</strong> Messages sent through the Platform between Users.</li>
            <li><strong>Support Communications:</strong> Information you provide when contacting our support team.</li>
          </ul>
          <h3>1.2 Information Collected Automatically</h3>
          <ul>
            <li><strong>Usage Data:</strong> Pages visited, features used, actions taken, date and time of access.</li>
            <li><strong>Device Information:</strong> Browser type, operating system, device type, screen resolution.</li>
            <li><strong>Log Data:</strong> IP address, access times, referring URLs, and error logs.</li>
            <li><strong>Cookies and Similar Technologies:</strong> See our <Link to="/cookie-policy" className="text-[#C85A42] hover:underline">Cookie Policy</Link> for details.</li>
          </ul>
          <h3>1.3 Information from Third Parties</h3>
          <ul>
            <li><strong>Social Login:</strong> If you sign in using Google, we receive your name, email address, and profile picture from Google.</li>
            <li><strong>Landlord-Provided Data:</strong> Landlords may provide your name, email, and phone number when adding you as a tenant before you create an account.</li>
          </ul>

          <h2>2. How We Use Your Information</h2>
          <p>We use the information we collect for the following purposes:</p>
          <ul>
            <li><strong>Provide and Maintain Services:</strong> Operate the Platform, process account registration, manage leases, track payments, and enable communication between Users.</li>
            <li><strong>Identity Verification:</strong> Verify tenant identities and match pre-registered tenant records with new registrations.</li>
            <li><strong>Notifications:</strong> Send service-related notifications (e.g., payment reminders, lease updates, new messages).</li>
            <li><strong>Analytics and Reporting:</strong> Generate financial reports, occupancy analytics, and bookkeeping data for Landlords.</li>
            <li><strong>Improve the Platform:</strong> Analyze usage patterns to improve features, performance, and user experience.</li>
            <li><strong>Legal Compliance:</strong> Comply with applicable laws, regulations, and legal processes.</li>
            <li><strong>Security:</strong> Detect, prevent, and address fraud, unauthorized access, and security incidents.</li>
            <li><strong>Communications:</strong> Respond to your inquiries and provide customer support.</li>
          </ul>

          <h2>3. How We Share Your Information</h2>
          <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
          <ul>
            <li><strong>Between Users:</strong> Tenant information is shared with their Landlord and assigned Property Managers as necessary for property management. Landlord contact information is shared with Tenants for communication purposes.</li>
            <li><strong>Service Providers:</strong> We use third-party providers for email delivery (Resend), cloud storage (iDrive e2), authentication (Google OAuth), and payment processing integration (PayPal). These providers access your data only as necessary to perform their services and are bound by confidentiality obligations.</li>
            <li><strong>Legal Requirements:</strong> We may disclose your information if required by law, regulation, legal process, or governmental request.</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of all or a portion of our assets, your information may be transferred as part of the transaction.</li>
            <li><strong>With Your Consent:</strong> We may share your information for purposes not described in this policy with your explicit consent.</li>
          </ul>

          <h2>4. Data Storage and Security</h2>
          <ul>
            <li>Your data is stored on secure cloud servers located in the United States.</li>
            <li>We implement industry-standard security measures including encryption in transit (TLS/SSL), secure password hashing (bcrypt), and access controls.</li>
            <li>File uploads (payment proofs, profile photos) are stored in encrypted cloud storage with access-controlled, time-limited URLs.</li>
            <li>While we strive to protect your information, no method of electronic storage or transmission is 100% secure. We cannot guarantee absolute security.</li>
          </ul>

          <h2>5. Data Retention</h2>
          <ul>
            <li>We retain your personal information for as long as your account is active or as needed to provide Services.</li>
            <li>Financial records (payments, receipts, bookkeeping data) may be retained for up to 7 years for tax and legal compliance purposes.</li>
            <li>Lease agreements and related documents are retained for the duration of the lease plus 3 years after termination.</li>
            <li>If you delete your account, we will delete or anonymize your personal data within 30 days, except where retention is required by law.</li>
            <li>Aggregated, anonymized data that cannot identify you may be retained indefinitely for analytics purposes.</li>
          </ul>

          <h2>6. Your Rights</h2>
          <h3>6.1 All Users</h3>
          <ul>
            <li><strong>Access:</strong> Request a copy of the personal information we hold about you.</li>
            <li><strong>Correction:</strong> Update or correct inaccurate personal information through your profile settings.</li>
            <li><strong>Deletion:</strong> Request deletion of your account and personal data (subject to legal retention requirements).</li>
            <li><strong>Export:</strong> Request a machine-readable copy of your data.</li>
          </ul>
          <h3>6.2 California Residents (CCPA/CPRA)</h3>
          <ul>
            <li>Right to know what personal information is collected, used, shared, or sold.</li>
            <li>Right to delete personal information (with certain exceptions).</li>
            <li>Right to opt-out of the sale or sharing of personal information. <strong>We do not sell your personal information.</strong></li>
            <li>Right to non-discrimination for exercising your privacy rights.</li>
            <li>Right to correct inaccurate personal information.</li>
            <li>Right to limit the use of sensitive personal information.</li>
          </ul>
          <h3>6.3 International Users (GDPR)</h3>
          <p>If you are located in the European Economic Area (EEA), United Kingdom, or similar jurisdictions, you additionally have the right to:</p>
          <ul>
            <li>Restrict processing of your personal data.</li>
            <li>Object to processing based on legitimate interests.</li>
            <li>Data portability.</li>
            <li>Withdraw consent at any time (where processing is based on consent).</li>
            <li>Lodge a complaint with a supervisory authority.</li>
          </ul>
          <p>Our legal bases for processing include: performance of a contract, legitimate interests, compliance with legal obligations, and consent.</p>

          <h2>7. Children's Privacy</h2>
          <p>The Platform is not intended for children under 18 years of age. We do not knowingly collect personal information from children under 18. If we become aware that we have collected data from a child under 18, we will take steps to delete such information promptly.</p>

          <h2>8. International Data Transfers</h2>
          <p>If you access the Platform from outside the United States, your information will be transferred to and processed in the United States. By using the Platform, you consent to this transfer. We take appropriate safeguards to ensure your data is protected in accordance with this Privacy Policy and applicable law.</p>

          <h2>9. Third-Party Links and Services</h2>
          <p>The Platform may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to review the privacy policies of any third-party services you use.</p>

          <h2>10. Changes to This Privacy Policy</h2>
          <p>We may update this Privacy Policy from time to time. Material changes will be communicated via email or through the Platform at least 30 days before taking effect. Your continued use of the Platform after changes take effect constitutes acceptance of the updated policy.</p>

          <h2>11. Contact Us</h2>
          <p>For privacy-related questions, data access requests, or to exercise your rights, contact us:</p>
          <div className="bg-muted/50 p-4 rounded-lg not-prose">
            <p className="font-semibold">MindfulUX — Privacy Team</p>
            <p className="text-sm text-muted-foreground">California, United States</p>
            <p className="text-sm">Email: <a href="mailto:info@mindfulux.org" className="text-[#C85A42] hover:underline">info@mindfulux.org</a></p>
          </div>

          <div className="mt-12 pt-8 border-t text-center text-sm text-muted-foreground not-prose">
            <p>&copy; {new Date().getFullYear()} MindfulUX. All rights reserved.</p>
            <div className="flex justify-center gap-4 mt-2">
              <Link to="/terms-of-service" className="text-[#C85A42] hover:underline">Terms of Service</Link>
              <Link to="/cookie-policy" className="text-[#C85A42] hover:underline">Cookie Policy</Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
